// This is the main LinePlot used in the workspaces and a key performance hotspot.
// When the user mouses over a LinePlot, we draw a "crosshair" (or "flag") with
// the legend in it, and bold the line that the user is mousing over. To do this
// in real-time, we render the LinePlot in layers. There are three layers:
// - The main layer, which renders all line data (including the currently bolded
//   lines but without bolding them).
// - The bold line layer, which renders just the bolded lines.
// - The crosshair layer, which renders the legend.
import React, {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useInView} from 'react-intersection-observer';
import useResizeObserver from 'use-resize-observer';

import {matchAllMetrics} from '../../state/interactState/constants';
import type {
  HighlightSource,
  SetHighlights,
} from '../../state/interactState/highlightSource';
import {type Highlight} from '../../state/interactState/types';
import {type PlotFontSize} from '../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../util/plotHelpers/plotHelpers';
import {type Timestep} from '../../util/plotHelpers/types';
import {type GraphTooltip} from '../PanelRunsLinePlot/graphRenderProps';
import {useLinePlotSettings} from '../PanelRunsLinePlot/LinePlotSettingsContext';
import {type UserZoomIntentPayload} from '../PanelRunsLinePlot/types';
import {finalizeBrushDrag} from '../PanelRunsLinePlot/zoom/brushAreaToUserZoom';
import {
  type ExcludeOutliersValues,
  isPointVizFullFidelity,
} from '../WorkspaceDrawer/Settings/types';
import {type AnnotationHoverSource, type AnnotationItem} from './annotations';
import {AxisTitleStrips, getAxisTitleInsets} from './AxisTitleStrips';
import {
  type NumericLine,
  presentNumericLines,
  type RenderLine,
} from './chartSeries';
import {clampLinePlotLines} from './clampLinePlotLines';
import {makeClampPoint} from './clampPoint';
import {ensureCrosshairGlobalStyles} from './crosshairGlobalStyles';
import {
  type BrushEndHandler,
  type BrushEndMetadata,
  type DomainArea,
} from './highlight';
import * as S from './LinePlot.styles';
import LinePlotCrosshair from './LinePlotCrosshair';
import {LinePlotPlot} from './LinePlotPlot';
import {LinePlotPlotWithHighlighting} from './LinePlotPlotWithHighlighting';
import {PositionedLayer} from './PlotBounds';
import {type Domain, type DomainMaybe} from './types';
import {getDomain as getuPlotDomain} from './uPlot/getDomain';
import {LinePlotuPlot, useShouldUseuPlot} from './uPlot/LinePlotuPlot';
import {getProfiledUPlotData} from './uPlot/prepareUPlotData';
import {useUPlotPreparationProfile} from './uPlot/useUPlotPreparationProfile';
import {useCalculatedDomain} from './useCalculatedDomain';
import {useHighlightValue} from './useHighlightValue';

const EMPTY_ISSUE_LINES: AnnotationItem[] = [];
const EMPTY_LINES: RenderLine[] = [];

interface LinePlotProps {
  children?: React.ReactNode;
  excludeOutliers?: ExcludeOutliersValues;
  filteredLines: RenderLine[];
  numericLines: NumericLine[];
  fontSize?: PlotFontSize;
  /** Cross-panel highlight (hovered run, metric, crosshair x). Must be stable. */
  highlightSource: HighlightSource;
  isDrawing: boolean;
  isHovered: boolean;
  issueLines: AnnotationItem[];
  /** Hovered issue for highlighting issue lines. Must be stable. */
  hoveredIssueSource: AnnotationHoverSource;
  /** The query zoom sets an x-axis max: hide 'after' issue lines. */
  isZoomedXMax: boolean;
  /** The query zoom sets an x-axis min: hide 'before' issue lines. */
  isZoomedXMin: boolean;
  /** Stable panel ID, sent with telemetry. */
  panelId: string;
  tooltip?: GraphTooltip;
  /** Writes the cross-panel highlight when the crosshair moves. Must be stable. */
  setHighlights: SetHighlights;
  setIsDrawing: (isDrawing: boolean) => void;
  svg?: boolean;
  timestep?: Timestep;
  xAxis: string; // Name of the xAxis
  xAxisTitle?: string; // optional string for exactly this xaxis title displayed in graph
  xDomain?: DomainMaybe;
  xScale?: 'linear' | 'log';
  yAxis: string; // Name of the yAxis
  yAxisTitle?: string; // optional string for exactly this yaxis title displayed in graph
  yDomain?: DomainMaybe;
  yScale?: 'linear' | 'log';
  zoomCallback?(
    zoom: UserZoomIntentPayload,
    brushEndMetadata?: BrushEndMetadata
  ): void;
  zooming?: boolean; // is zooming happening
}

function isNullDomain(domain: DomainMaybe): domain is Domain {
  return domain[0] === null || domain[1] === null;
}

const noOpIssueLines: AnnotationItem[] = [];

const LinePlotComp: FC<LinePlotProps> = ({
  excludeOutliers,
  filteredLines: linesForOverlay,
  numericLines,
  fontSize = 'small',
  highlightSource,
  isDrawing,
  isHovered,
  issueLines,
  hoveredIssueSource,
  isZoomedXMax,
  isZoomedXMin,
  panelId,
  tooltip,
  setHighlights,
  setIsDrawing,
  svg,
  timestep,
  xAxis,
  xAxisTitle,
  xDomain: userXDomain,
  xScale = 'linear',
  yAxis,
  yAxisTitle,
  yDomain: userYDomain,
  yScale = 'linear',
  zoomCallback,
}: LinePlotProps) => {
  // state
  const [hideCrosshair, setHideCrosshair] = useState(false);
  const [lastDrawLocation, setLastDrawLocation] = useState<DomainArea | null>(
    null
  );
  const {height, ref: innerRef, width} = useResizeObserver<HTMLDivElement>();

  // Inject the crosshair's global stylesheet once, outside the per-move render
  // path. These rules previously lived in inline <style> elements rendered
  // inside the crosshair subtree, which re-mounts on every mousemove; churning
  // the document stylesheet set forced a full-document style recalc per move.
  useEffect(() => {
    ensureCrosshairGlobalStyles();
  }, []);

  // hooks
  const {
    isMultiMetric,
    pointVisualizationMethod,
    xAxisIsDatetime,
    xLogScale,
    plotType,
  } = useLinePlotSettings();
  // The run and metric decide dimming here. The crosshair x is read by the
  // crosshair itself, so a crosshair move does not re-render LinePlot.
  const [domRef, onScreen] = useInView();
  const activeRun = useHighlightValue(highlightSource, 'run:name', onScreen) as
    | string
    | undefined;
  const activeMetric = useHighlightValue(
    highlightSource,
    'run:metric',
    onScreen
  ) as Highlight['run:metric'];

  // derived values
  const isHeatmap = numericLines[0]?.kind === 'heatmap';
  const [calcXDomain, calcYDomain] = useCalculatedDomain({
    filteredLines: numericLines,
    excludeOutliers,
    isFullFidelityMode: isPointVizFullFidelity(pointVisualizationMethod),
    isHeatmap,
    userXDomain,
    userYDomain,
  });

  /**
   * TODO: These are cast as Domain but the types are actually DomainMaybe
   */
  const notNullXDomain = calcXDomain as Domain;
  const notNullYDomain = calcYDomain as Domain;

  const crosshairXDomain = useMemo(() => {
    return isHeatmap
      ? [notNullXDomain[0] - 0.5, notNullXDomain[1] + 0.5]
      : notNullXDomain;
  }, [isHeatmap, notNullXDomain]);

  /**
   * Notes on point clamping:
   * There are a couple of different scenarios that point clamping is intended to address:
   * 1a) If non-finite values are fed into the chart (Inf, -Inf) we need to avoid them blowing out chart scaling so you can't see the line
   * 1b) Similarly, if just a couple really out of bounds outlier values come in, we need to avoid those blowing out the chart scaling too
   * 2) If a user has provided explicit boundaries on the chart (either w/ config values or with a zoom event) we need to fix _exactly_ to those boundaries so that chart points don't render outside the chart area.
   *
   * TODO: investigate if the 1b scenario still is possible given changes to exclude outliers mode
   */
  const pointClamper = useMemo(() => {
    const safeYDomain = calcYDomain as Domain;

    return makeClampPoint(
      [safeYDomain[0], safeYDomain[1]],
      userYDomain
        ? [safeYDomain[0], safeYDomain[1]]
        : [safeYDomain[0] - 100000, safeYDomain[1] + 100000] // these are the fudged boundaries to avoid
    );
  }, [calcYDomain, userYDomain]);

  // After calculating the domain, let's filter the lines a bit again. We want to avoid
  // a bug (https://github.com/wandb/core/issues/2323) where lines aren't drawn when
  // there are extreme outliers present. So let's clamp points to an offset of the domain.
  // We'll still use the unclamped points in the crosshair so as to not obviously present "wrong" data.
  const clampedFilteredLines: NumericLine[] = useMemo(
    function clampLineValues() {
      if (isNullDomain(calcYDomain)) {
        return numericLines;
      }
      return clampLinePlotLines(numericLines, pointClamper);
    },
    [numericLines, calcYDomain, pointClamper]
  );

  // if user passed in multiple heatmaps only display the first one and nothing else
  const numericLinesOrHeatmap = useMemo(
    function linesOrHeatMapMemo() {
      const heatmap = clampedFilteredLines.find(
        line => line.kind === 'heatmap'
      );
      if (heatmap == null) {
        return clampedFilteredLines;
      }
      return [heatmap];
    },
    [clampedFilteredLines]
  );

  const linesOrHeatmap = useMemo(
    () => presentNumericLines(numericLinesOrHeatmap, linesForOverlay),
    [numericLinesOrHeatmap, linesForOverlay]
  );

  const handleCrosshairBrushEnd: BrushEndHandler = useCallback(
    function handleCrossHairBrushEndCallback(
      area,
      zoomIntent,
      brushEndMetadata: BrushEndMetadata = {}
    ) {
      finalizeBrushDrag({
        area,
        brushEndMetadata,
        interrupted: brushEndMetadata.interrupt,
        setLastDrawLocation,
        timestep,
        xAxisIsDatetime,
        zoomCallback,
        zoomIntent,
      });
    },
    [timestep, zoomCallback, xAxisIsDatetime]
  );

  const handleCrosshairMouseUp = useCallback(() => {
    setHideCrosshair(false);
  }, []);
  const handleCrosshairMouseDown = useCallback(() => {
    setHideCrosshair(true);
  }, []);

  /**
   * Dimming exists if:
   * - there is a valid run in an active hover state
   * - we are not in a single run view
   * - and the feature flag is flipped
   */
  const isDimmed = !!activeRun;
  const dimFactor = !isDimmed ? undefined : 0.3;

  const dataOutOfBoundsX = dataOutOfBounds(notNullXDomain, userXDomain);
  const dataOutOfBoundsY = dataOutOfBounds(notNullYDomain, userYDomain);

  const highlightedSeriesIds = React.useMemo(
    function getLinesForHighlighting() {
      const highlightedLines = numericLinesOrHeatmap.filter(l => {
        // Include the actively hovered run
        const isActiveRun =
          (l.highlightId ?? undefined) === activeRun ||
          (l.highlightName ?? undefined) === activeRun;
        // Also include baseline run to keep it at full opacity
        // Note: pinned runs should dim like other non-hovered runs
        const isBaseline = l.isBaseline;

        if (isMultiMetric) {
          const isActiveMetric =
            l.metricName === activeMetric || activeMetric === matchAllMetrics;
          return (isActiveRun || isBaseline) && isActiveMetric;
        }

        return isActiveRun || isBaseline;
      });

      return new Set(highlightedLines.map(line => line.seriesId));
    },
    [activeRun, activeMetric, numericLinesOrHeatmap, isMultiMetric]
  );

  // Match presentation to numeric highlight membership by required drawable ID.
  // The uPlot overlay reads the stable ID set without rebuilding its data layers.
  const linesForHighlighting = linesOrHeatmap.filter(line =>
    highlightedSeriesIds.has(line.seriesId)
  );

  const shouldUseuPlot = useShouldUseuPlot(
    numericLinesOrHeatmap,
    plotType,
    panelId
  );
  const hasValidDomain =
    calcXDomain[0] != null &&
    calcXDomain[1] != null &&
    calcYDomain[0] != null &&
    calcYDomain[1] != null;
  const canRender =
    linesForOverlay.length > 0 &&
    hasValidDomain &&
    !dataOutOfBoundsX &&
    !dataOutOfBoundsY;
  const canPrepareUPlot =
    canRender && shouldUseuPlot && width != null && height != null;
  const profiledUPlotData = useMemo(
    function prepareChartData() {
      return canPrepareUPlot
        ? getProfiledUPlotData(numericLinesOrHeatmap, xLogScale)
        : undefined;
    },
    [canPrepareUPlot, numericLinesOrHeatmap, xLogScale]
  );
  const preparedUPlotData = profiledUPlotData?.preparedData;
  useUPlotPreparationProfile(
    panelId,
    profiledUPlotData?.measurement,
    yScale === 'log',
    width,
    height
  );

  const [plotMarginOverride, setPlotMarginOverride] = useState<
    PlotMargin | undefined
  >();

  if (linesForOverlay.length === 0) {
    return null;
  }

  if (!hasValidDomain) {
    return <S.InvalidDataAlert>Unable to process data.</S.InvalidDataAlert>;
  }

  if (dataOutOfBoundsX || dataOutOfBoundsY) {
    return (
      <S.InvalidDataAlert>
        Data out of bounds. Try adjusting the axis range.
      </S.InvalidDataAlert>
    );
  }

  if (shouldUseuPlot) {
    // if the height or width is not measured yet, we need to return a placeholder div because uPlot needs the correct dimensions
    // in order to render the plot.
    if (height == null || width == null) {
      return <div ref={innerRef} style={{width: '100%', height: '100%'}} />;
    }

    const uPlotCrosshairXDomain = getuPlotDomain(
      crosshairXDomain as DomainMaybe,
      xScale === 'log'
    ) as Domain;

    // Reserve out-of-plot strips for the axis titles so they no longer overlap
    // the chart. The plot layers live in an offset cell; because innerRef's
    // ResizeObserver measures that cell, `width`/`height` above are already the
    // reduced plot size — every layer + the crosshair use them as-is. When a
    // title is absent its strip is 0px, so the plot fills the full container.
    const xAxisTitleText = xAxisTitle || xAxis;
    const axisTitleInsets = getAxisTitleInsets(fontSize, {
      xTitle: xAxisTitleText,
      yTitle: yAxisTitle,
    });

    const sharedProps = {
      width,
      height,
      xDomain: calcXDomain,
      yDomain: calcYDomain,
      xAxis,
      fontSize,
      panelId,
      timestep,
      plotMarginOverride,
    };

    // Use four layers like react-vis:
    // 1. Base Layer with plot only
    // 2. Line layer with all lines (dimmed via opacity)
    // 3. Highlighted layer with only highlighted lines (on top)
    // 4. Tooltip layer (on top of everything, not dimmed)
    return (
      <PositionedLayer ref={domRef}>
        <AxisTitleStrips
          fontSize={fontSize}
          xTitle={xAxisTitleText}
          yTitle={yAxisTitle}
          plotCellHeight={height}
        />
        {/* Plot cell — offset by the title strips so its measured size (via
            innerRef) is the reduced plot area. */}
        <div
          className="absolute right-0 top-0"
          style={{left: axisTitleInsets.left, bottom: axisTitleInsets.bottom}}>
          {/* Base Layer - Draws Plot */}
          <PositionedLayer
            pointerEvents="none"
            dim={
              !!activeRun && linesForHighlighting.length === 0
                ? dimFactor
                : undefined
            }>
            <LinePlotuPlot
              lines={EMPTY_LINES}
              issueLines={EMPTY_ISSUE_LINES}
              isBaseLayer
              {...sharedProps}
            />
          </PositionedLayer>
          {/* Layer with all lines - when there are highlighted lines, no tooltip */}
          <PositionedLayer ref={innerRef} dim={dimFactor}>
            <LinePlotuPlot
              lines={linesOrHeatmap}
              preparedData={preparedUPlotData}
              issueLines={issueLines}
              hoveredIssueSource={hoveredIssueSource}
              isZoomedXMin={isZoomedXMin}
              isZoomedXMax={isZoomedXMax}
              dimFactor={dimFactor}
              setPlotMarginOverride={setPlotMarginOverride}
              {...sharedProps}
            />
          </PositionedLayer>

          {/* Highlighted layer - rendered on top, drawn bright over the dimmed base.
              Always mounted with the full line set; which lines are visible is
              toggled imperatively (per-series `show`) from highlightedSeriesIds, so
              hovering never changes this layer's options identity and therefore
              never recreates the uPlot instance (the source of the per-hover forced
              "Recalculate Style"). */}
          <PositionedLayer pointerEvents="none">
            <LinePlotuPlot
              lines={linesOrHeatmap}
              preparedData={preparedUPlotData}
              issueLines={EMPTY_ISSUE_LINES}
              isOverlay
              highlightedSeriesIds={highlightedSeriesIds}
              {...sharedProps}
            />
          </PositionedLayer>

          {/* Tooltip layer - on top of everything, not dimmed */}
          <PositionedLayer>
            {height !== 0 && (
              <LinePlotCrosshair
                activeMetric={activeMetric}
                activeRun={activeRun}
                fontSize={fontSize}
                height={height || 200}
                hideCrosshair={hideCrosshair}
                highlightSource={highlightSource}
                isDrawing={isDrawing}
                isHovered={isHovered ?? false}
                lastDrawLocation={lastDrawLocation}
                lines={linesForOverlay}
                numericLines={numericLines}
                onBrushEnd={handleCrosshairBrushEnd}
                onMouseDown={handleCrosshairMouseDown}
                onMouseUp={handleCrosshairMouseUp}
                onScreen={onScreen}
                plotMargin={plotMarginOverride}
                pointClamper={pointClamper}
                renderer="uplot"
                tooltip={tooltip}
                setHighlights={setHighlights}
                setIsDrawing={setIsDrawing}
                width={width || 200}
                xAxis={xAxis}
                xDomain={uPlotCrosshairXDomain}
                xScale={xScale}
                yAxis={yAxis}
                yDomain={notNullYDomain}
                yScale={yScale}
              />
            )}
          </PositionedLayer>
        </div>
      </PositionedLayer>
    );
  }

  return (
    <PositionedLayer ref={domRef}>
      {/**
       * Note: this used to be wrapped in DelayedRender which was originally intended for a bug where React Vis wouldn't size correctly when siblings were added and removed to the component containing React-Vis (https://github.com/laxels/flex-grow-react-sibling). I believe this was the legend, but I've moved that up a level and have reason to believe that the original conditions requiring DelayedRender no longer exist.
       *
       * IF we get a bug where React Vis isn't sizing correctly in the line-plots please see:
       * https://github.com/wandb/core/pull/18454
       *
       * For performance we render multiple layers. This way we don't have to re-render the entire plot when the highlighted lines change.
       */}

      {/* Base layer - draws only the grid/axes. Kept at full opacity (unless
          nothing in this plot is highlighted) so gridlines stay crisp while the
          line layers above dim on hover. Mirrors the uPlot isBaseLayer plot.
          Skipped for heatmaps: react-vis expands the domain by half a cell to
          center heatmap rows (HeatmapSeries.getParentConfig), an adjustment the
          empty base layer can't reproduce, so its grid/ticks would sit at the
          unadjusted domain and no longer line up with the heatmap. Heatmaps
          don't get run-hover dimming anyway, so they draw their own chrome. */}
      {!isHeatmap && (
        <PositionedLayer
          pointerEvents="none"
          dim={
            isDimmed && linesForHighlighting.length === 0
              ? dimFactor
              : undefined
          }>
          {height !== 0 && (
            <LinePlotPlot
              drawing={isDrawing}
              dontCheckIfEmpty
              fontSize={fontSize}
              height={height || 200}
              issueLines={noOpIssueLines}
              isZoomedXMax={isZoomedXMax}
              isZoomedXMin={isZoomedXMin}
              lines={EMPTY_LINES}
              panelId={panelId}
              svg={svg}
              width={width || 200}
              xAxis={xAxis}
              xAxisTitle={xAxisTitle}
              xDomain={notNullXDomain}
              xScale={xScale}
              yAxis={yAxis}
              yAxisTitle={yAxisTitle}
              yDomain={notNullYDomain}
              yScale={yScale}
              timestep={timestep}
            />
          )}
        </PositionedLayer>
      )}

      <PositionedLayer dim={dimFactor} ref={innerRef}>
        {height !== 0 && (
          <LinePlotPlot
            drawing={isDrawing}
            fontSize={fontSize}
            height={height || 200}
            issueLines={issueLines}
            hoveredIssueSource={hoveredIssueSource}
            isZoomedXMax={isZoomedXMax}
            isZoomedXMin={isZoomedXMin}
            lines={linesOrHeatmap}
            panelId={panelId}
            showPlotChrome={isHeatmap}
            svg={svg}
            width={width || 200}
            xAxis={xAxis}
            xAxisTitle={xAxisTitle}
            xDomain={notNullXDomain}
            xScale={xScale}
            yAxis={yAxis}
            yAxisTitle={yAxisTitle}
            yDomain={notNullYDomain}
            yScale={yScale}
            timestep={timestep}
          />
        )}
      </PositionedLayer>

      <PositionedLayer>
        {height !== 0 && (
          <LinePlotPlotWithHighlighting
            drawing={isDrawing}
            fontSize={fontSize}
            height={height || 200}
            issueLines={noOpIssueLines}
            isZoomedXMax={isZoomedXMax}
            isZoomedXMin={isZoomedXMin}
            lines={linesForHighlighting}
            panelId={panelId}
            svg={svg}
            width={width || 200}
            xAxis={xAxis}
            xAxisTitle={xAxisTitle}
            xDomain={notNullXDomain}
            xScale={xScale}
            yAxis={yAxis}
            yAxisTitle={yAxisTitle}
            yDomain={notNullYDomain}
            yScale={yScale}
            timestep={timestep}
          />
        )}
      </PositionedLayer>

      <PositionedLayer>
        {height !== 0 && (
          <LinePlotCrosshair
            activeMetric={activeMetric}
            activeRun={activeRun}
            fontSize={fontSize}
            height={height || 200}
            hideCrosshair={hideCrosshair}
            highlightSource={highlightSource}
            isDrawing={isDrawing}
            isHovered={isHovered ?? false}
            lastDrawLocation={lastDrawLocation}
            lines={linesForOverlay}
            numericLines={numericLines}
            onBrushEnd={handleCrosshairBrushEnd}
            onMouseDown={handleCrosshairMouseDown}
            onMouseUp={handleCrosshairMouseUp}
            onScreen={onScreen}
            pointClamper={pointClamper}
            renderer="react-vis"
            tooltip={tooltip}
            setHighlights={setHighlights}
            setIsDrawing={setIsDrawing}
            width={width || 200}
            xAxis={xAxis}
            xDomain={crosshairXDomain}
            xScale={xScale}
            yAxis={yAxis}
            yDomain={notNullYDomain}
            yScale={yScale}
          />
        )}
      </PositionedLayer>
    </PositionedLayer>
  );
};

export const LinePlot = memo(LinePlotComp);

function dataOutOfBounds(calcDomain: Domain, userDomain?: DomainMaybe) {
  if (userDomain == null || calcDomain == null) {
    return false;
  }

  const [boundsMin, boundsMax] = userDomain;
  const [dataMin, dataMax] = calcDomain;

  const boundsMaxSmallerThanDataMin =
    boundsMax != null && dataMin != null && boundsMax < dataMin;
  const boundsMinBiggerThanDataMax =
    boundsMin != null && dataMax != null && boundsMin > dataMax;

  return boundsMaxSmallerThanDataMin || boundsMinBiggerThanDataMax;
}
